/**
 * Prose source attribution for report narratives and deterministic fallbacks.
 * Factual claims should lead with where the data came from (provider, document,
 * or SED section) before stating the measurement.
 */
import type { ConnectivityEvidence, EvidenceSection, GridEvidence } from '../types/siteEvidence';

function humanizeProviderKey(key: string | undefined | null): string {
  if (!key || typeof key !== 'string') return 'Provider';
  const k = key.toLowerCase().replace(/\s+/g, '-');
  if (k.includes('wri') || k.includes('aqueduct')) return 'WRI Aqueduct';
  if (k.includes('fema')) return 'FEMA NFHL';
  if (k.includes('nrel')) return 'NREL';
  if (k.includes('usgs')) return 'USGS';
  if (k.includes('eia')) return 'EIA';
  if (k.includes('hifld')) return 'HIFLD';
  if (k.includes('regrid')) return 'Regrid';
  if (k.includes('open-meteo') || k.includes('noaa')) return 'NOAA / Open-Meteo';
  if (k.includes('peeringdb')) return 'PeeringDB';
  if (k.includes('airnow') || k.includes('aqi')) return 'AirNow';
  if (k.includes('usfws')) return 'USFWS';
  if (k.includes('nrc') || k.includes('adams')) return 'NRC ADAMS';
  const fromSource = key.split('—')[0]?.trim();
  if (fromSource && fromSource.length > 2 && !fromSource.includes('unavailable')) {
    return fromSource.replace(/\s+\(cached\)$/i, '');
  }
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Opening clause: "According to data available from PeeringDB," */
export function accordingToDataFrom(sourceLabel: string): string {
  const label = sourceLabel.trim() || 'the cited provider';
  return `According to data available from ${label},`;
}

export function resolveEvidenceSourceLabel(
  section: Pick<EvidenceSection<unknown>, 'source' | 'attemptedProvider'> | undefined | null,
  fallback = 'the cited provider',
): string {
  if (!section) return fallback;
  if (section.attemptedProvider) return humanizeProviderKey(section.attemptedProvider);
  if (section.source) return humanizeProviderKey(section.source);
  return fallback;
}

export function isVerifiedEvidenceSection<T>(
  section: EvidenceSection<T> | undefined | null,
): section is EvidenceSection<T> & { data: T } {
  if (!section || section.status !== 'verified' || section.data == null) return false;
  if (section.attemptResult && section.attemptResult !== 'success') return false;
  const src = (section.source ?? '').toLowerCase();
  if (src.includes('regional estimate') || src.includes('temporarily unavailable')) return false;
  return true;
}

type IxFields = Pick<
  ConnectivityEvidence,
  'nearestIxName' | 'nearestIxDistKm' | 'ixCount50km' | 'facilityCount50km'
> & { nearestIxCity?: string; nearestIxKind?: ConnectivityEvidence['nearestIxKind'] };

/** Investment-grade IX paragraph with explicit provider attribution. */
export function formatInternetExchangeNarrative(
  data: IxFields,
  sourceLabel = 'PeeringDB',
): string {
  const attr = accordingToDataFrom(sourceLabel);
  const dist =
    typeof data.nearestIxDistKm === 'number' && Number.isFinite(data.nearestIxDistKm)
      ? `${data.nearestIxDistKm.toFixed(1)} km`
      : 'an unverified distance';
  const city = data.nearestIxCity?.trim();
  const name = data.nearestIxName?.trim() || 'the nearest Internet Exchange';
  const location = city ? `${name} in ${city}` : name;
  if (data.nearestIxKind === 'colo-fallback') {
    return `${attr} the nearest colocation facility returned as an IX-query fallback is ${location}, ${dist} away geographically. An actual IX, fiber route, carrier service and network latency remain unverified.`;
  }
  const marketPhrase = typeof data.ixCount50km === 'number'
    ? ` PeeringDB lists ${data.ixCount50km} IX(s) within 50 km.` : '';
  return `${attr} the nearest Internet Exchange is ${location}, located ${dist} away geographically.${marketPhrase} Geographic proximity does not establish a fiber route, carrier service or measured latency.`;
}

export function formatConnectivityUnavailable(): string {
  return 'Internet exchange proximity is not verified for this site. PeeringDB data was unavailable or incomplete — commission a site-specific connectivity study before stating IX distance or market structure.';
}

export function formatAttributedInternetExchangeFromSed(
  section: EvidenceSection<ConnectivityEvidence> | undefined | null,
): string {
  if (!isVerifiedEvidenceSection(section)) return formatConnectivityUnavailable();
  return formatInternetExchangeNarrative(
    section.data,
    resolveEvidenceSourceLabel(section, 'PeeringDB'),
  );
}

export function formatAttributedDatacenterFacilities(
  data: Pick<ConnectivityEvidence, 'facilityCount50km'>,
  sourceLabel = 'PeeringDB',
): string {
  const attr = accordingToDataFrom(sourceLabel);
  const count = data.facilityCount50km;
  if (typeof count !== 'number' || !Number.isFinite(count)) {
    return `${attr} colocation facility counts for this site were not returned — verify with a carrier survey.`;
  }
  if (count >= 5) {
    return `${attr} ${count} data center/colocation facilities lie within 50 km. A strong regional DC ecosystem may support cloud on-ramps (AWS Direct Connect, Azure ExpressRoute, GCP Interconnect), subject to carrier confirmation.`;
  }
  if (count >= 1) {
    return `${attr} ${count} data center/colocation facilit${count === 1 ? 'y lies' : 'ies lie'} within 50 km. Verify cloud on-ramp availability with carriers.`;
  }
  return `${attr} no colocation facilities were listed within 50 km. This facility count does not establish local fiber availability; verify carrier routes independently.`;
}
